import { Box, Button, Grid, Hidden, Skeleton, Tab, TabProps, Tabs, Theme, Typography, useTheme } from "@mui/material";
import { useHistory, useLocation } from "react-router";
import { CustomeModalButton } from "../../../lib/formik/NewFormInputs";
import { CSSObject, maxWidth, SxProps } from "@mui/system";
import { ChartIcon, CurrencyIcon, MoneyGrow, PercentageIcons, TotalInvestors } from "../../../assets/SvgIcons";
import { useEffect, useState } from "react";
import useQuery from "../../../utils/useQuery";
import OverView from "./OverView";
import Mandates from "./mandates";
import Holdings from "./holdings";
import Orders from "./orders";
import Recommendations from "./recommendations";
import { useDispatch, useSelector } from "react-redux";
import {
    setAppliedFundsFilters,
    setAppliedHoldingsFilters,
    setAppliedSystematicPlansFilters,
    setCheckedHoldingSchemes,
    setExploreMutualFundsPageVisited,
    setHoldingsActiveCategory,
    setSelectedAllHoldingsInfo,
    setSelectedHoldingsIds,
    setSelectedHoldingsInfo,
    setSelectedSchemesObj,
    setSystematicPlansSearchText
} from "../../../redux-store/actions/investorProfile";
import BreadCrum from "../../../lib/BreadCrum";
import { RootStateType } from "../../../redux-store/reducers";
import { navigationData } from "../types";
import { CategoryWiseDistibutionRes, TotalCardValuesRes } from "../../../redux-store/types/products";
import { getCategoryWiseDistribution, totalCardValuesApi } from "../../../redux-store/actions/product";
import { MPOWER_ACCOUNT_TYPE_MASTER, ROLE_TYPE, USER_ROLES } from "../../../utils/constant";
import { formatCurrency, isModuleAllowed } from "../../../utils/utilityFunctions";
import { ZeroStateInvestment } from "../common/ZeroStateInvestment";
import SystematicPlans from "./systematicPlans";
import { TabContext } from "@mui/lab";

const tabsList = ['Overview', 'Holdings', 'Orders', 'Recommendations', 'Manage Mandates', 'Systematic Plans']

export default function MutualFund(): JSX.Element {
    const dispatch = useDispatch()
    const location = useLocation()
    const query = useQuery()
    const { palette, typography } = useTheme()
    const history = useHistory();
    const { pathname: pathName, state } = history.location

    const {
        role = '',
        subRole = '',
        permissions = []
    } = useSelector((store: RootStateType) => store.auth);
    const {
        selectedPortfolio,
        selectedPortfolioObj
    } = useSelector((store: RootStateType) => store.selectedPortfiolio);
    const { iwappRmId, investorClientId, isPortfolioAvailable = false } = useSelector((store: RootStateType) => store.id)
    const { investorRmId = "" } = useSelector((store: RootStateType) => store.sessionData);
    const {
        selectAllHoldingsInfo
    } = useSelector((store: RootStateType) => store.investorProfile);
    const { locale, currencyCode } = useSelector((store: RootStateType) => store.currencyInfo);

    const navState = state as navigationData

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [selectTab, setSelectTab] = useState(query.get('tab') || 'Overview')
    const [cardValues, setCardValues] = useState<TotalCardValuesRes>({} as TotalCardValuesRes)
    const [portfolioDoesntExist, setPortfolioDoesntExist] = useState<boolean>(false);
    const [zeroStateCheck, setZeroStateCheck] = useState<boolean>(false);



    const totalCardValuesInvestorLevel = async () => {
        try {
            setIsLoading(true);

            const cardValuesRes = await (dispatch(totalCardValuesApi({
                rmId: `${investorRmId}`,
                category: '',
                clientCode: ![USER_ROLES.INVESTOR].includes(role) ? navState?.clientCode : investorClientId,
                portfolioId: selectedPortfolio?.value,
                assetGroup: "MF"
            })) as unknown as Promise<TotalCardValuesRes>)

            if (cardValuesRes?.isSuccess) {
                setCardValues(cardValuesRes)
                if (cardValuesRes?.result?.totalInvestedValue === 0) {
                    setPortfolioDoesntExist(true)
                }
            } else {
                setZeroStateCheck(true);
            }

        } catch (e) {
            console.log("Error", e);
        } finally {
            setIsLoading(false);
        }
    }


    useEffect(() => {
        totalCardValuesInvestorLevel();
    }, [])

    const handleChangeTab = (event: React.SyntheticEvent, value: string) => {
        setSelectTab(value)

        if (value === "Systematic Plans") {
            dispatch(setSystematicPlansSearchText(""))
            dispatch(setAppliedSystematicPlansFilters([]))

            history.replace({
                pathname: location.pathname, search: `?tab=${value}&type=SIP`, state: {
                    clientCode: navState?.clientCode ?? ""
                }
            })
        } else {
            history.replace({
                pathname: location.pathname, search: `?tab=${value}`, state: {
                    clientCode: navState?.clientCode ?? ""
                }
            })
        }
    }

    const handleExploreMutualFunds = () => {

        dispatch(setSelectedAllHoldingsInfo([]))

        dispatch(setAppliedFundsFilters([]));
        dispatch(setSelectedSchemesObj({}));
        dispatch(setExploreMutualFundsPageVisited(false));

        dispatch(setAppliedHoldingsFilters([]))
        dispatch(setHoldingsActiveCategory("All"))
        dispatch(setSelectedHoldingsIds([]))
        dispatch(setCheckedHoldingSchemes([]))
        dispatch(setSelectedHoldingsInfo([]))

        dispatch(setSystematicPlansSearchText(""))
        dispatch(setAppliedSystematicPlansFilters([]))

        history.push(`${pathName}/explore`, {
            clientCode: navState?.clientCode || ""
        })
    }

    const initialRouter = pathName.split('/').filter((item) => item !== '')

    // console.log('path', initialRouter, pathName)

    const rmRouterPath = (isModuleAllowed({ value: "MFDashboard", compareArr: permissions, objKey: "permissonCode" }) ? [
        {
            name: 'Dashboard',
            path: '/dashboard',
            warning: selectTab === "Holdings" && selectAllHoldingsInfo.length > 0,
            handleClick: () => {
                dispatch(setSelectedAllHoldingsInfo([]))
            }
        },
        {
            name: `${initialRouter[1]} ${selectedPortfolio.label ? `(${selectedPortfolio.label})` : ''}`,
            path: `/${initialRouter[0]}/${initialRouter[1]}`,
            state: {
                clientCode: navState?.clientCode || ""
            },
            warning: selectTab === "Holdings" && selectAllHoldingsInfo.length > 0,
            handleClick: () => {
                dispatch(setSelectedAllHoldingsInfo([]))
            }
        },
        {
            name: 'Mutual Fund',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`,
            state: {
                clientCode: navState?.clientCode || ""
            }
        }
    ] : [])

    const investorRouterPath = [
        {
            name: `Dashboard ${selectedPortfolio.label ? `(${selectedPortfolio.label})` : ''}`,
            path: '/investor-dashboard',
            warning: selectTab === "Holdings" && selectAllHoldingsInfo.length > 0,
            handleClick: () => {
                dispatch(setSelectedAllHoldingsInfo([]))
            }
        },
        {
            name: 'Mutual Fund',
            path: `/${initialRouter[0]}/${initialRouter[1]}/${initialRouter[2]}`,
            state: {
                clientCode: navState?.clientCode || ""
            }
        }
    ]

    // If subRole is not there (Investor case), then => NOT DISABLED
    // If role === IA or subRole === IA, then => NOT DISABLED
    // Otherwise => 
    //    - portfolioAccountType === ADVISORY/POA => NOT DISABLED
    //    - portfolioAccountType !== ADVISORY/POA => DISABLED  

    const isExploreMutualFundDisabled = subRole ?
        (
            isModuleAllowed({ value: `${role ?? ""}`, compareArr: [USER_ROLES.IA] }) ||
            isModuleAllowed({ value: `${subRole ?? ""}`, compareArr: [ROLE_TYPE.IA] })
        )
            ? false :
            !([
                MPOWER_ACCOUNT_TYPE_MASTER[MPOWER_ACCOUNT_TYPE_MASTER.ADVISORY],
                MPOWER_ACCOUNT_TYPE_MASTER[MPOWER_ACCOUNT_TYPE_MASTER.POA]
            ].includes(MPOWER_ACCOUNT_TYPE_MASTER[`${selectedPortfolioObj?.portfolioAccountType ?? ""}`?.toUpperCase()]))
        : false

    return (
        <Box>
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'column', md: 'row', lg: 'row', xl: 'row' }, }}>
                <Box sx={{ width: '100%' }}>
                    <BreadCrum array={![USER_ROLES.INVESTOR].includes(role) ? rmRouterPath : investorRouterPath} />
                </Box>
                <br />
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: {
                            xs: 'space-between',
                            md: 'flex-end',
                        },
                        width: '100%',
                        alignItems: 'center'
                    }}>
                    <Hidden smUp={true}>
                        <Typography variant="overline_600" fontSize={'16px'} color={palette.text.primary}>{selectTab}</Typography>
                    </Hidden>
                    <Button
                        onClick={handleExploreMutualFunds}
                        variant="contained"
                        sx={(theme: Theme) => ({
                            borderRadius: "10px",
                            padding: "10px 15px",
                            backgroundColor: theme.palette.primary[700],
                            color: theme.palette.textDark.primary,
                            maxWidth: '250px',
                            "&.Mui-disabled": {
                                backgroundColor: `${theme.palette.text.secondary}B2`,
                                color: theme.palette.text.secondary,
                                cursor: 'not-allowed',
                                pointerEvents: 'all',
                            },
                            flexWrap: 'nowrap',
                        })}
                        disabled={
                            ([USER_ROLES.INVESTOR].includes(role) && !isPortfolioAvailable) ||
                            isExploreMutualFundDisabled
                        }
                    >
                        Explore <Hidden only={'xs'}> Mutual Funds</Hidden>
                    </Button>

                </Box>
            </Box>

            <Hidden only={'xs'}>
                <Grid container spacing={2} mt={'25px'}>
                    <Grid item width={'100%'} sm={6} lg={3}>
                        <Card
                            Icons={MoneyGrow}
                            title="Market Value"
                            percent={false}
                            value={cardValues?.result?.totalMarketValue < 0 ? `- ${formatCurrency({ value: Math.abs(cardValues?.result?.totalMarketValue ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: cardValues?.result?.totalMarketValue ?? 0, locale, currencyCode })}`}
                            isLoading={isLoading}
                        />
                    </Grid>
                    <Grid item width={'100%'} sm={6} lg={3}>
                        <Card
                            Icons={CurrencyIcon}
                            title="Invested Value"
                            percent={false}
                            value={cardValues?.result?.totalInvestedValue < 0 ? `- ${formatCurrency({ value: Math.abs(cardValues?.result?.totalInvestedValue ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: cardValues?.result?.totalInvestedValue ?? 0, locale, currencyCode })}`}
                            isLoading={isLoading}
                        />
                    </Grid>
                    <Grid item width={'100%'} sm={6} lg={3}>
                        <Card
                            Icons={ChartIcon}
                            title="Gain/Loss"
                            value={cardValues?.result?.gainLoss < 0 ? `- ${formatCurrency({ value: Math.abs(cardValues?.result?.gainLoss ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: cardValues?.result?.gainLoss ?? 0, locale, currencyCode })}`}
                            color={Number(cardValues?.result?.gainLoss ?? 0) >= 0 ? palette.success[800] : palette.error[500]}
                            isLoading={isLoading}
                        />
                    </Grid>
                    <Grid item width={'100%'} sm={6} lg={3}>
                        <Card
                            Icons={PercentageIcons}
                            value={!Number.isNaN(Number(cardValues?.result?.percentage)) ?
                                `${Math.round(cardValues?.result?.percentage * 100) / 100} %` : '0%'}
                            title="Absolute Return %"
                            percent={false}
                            isLoading={isLoading}
                            color={Number(cardValues?.result?.percentage ?? 0) >= 0 ? palette.success[800] : palette.error[500]}
                        />
                    </Grid>
                </Grid>
            </Hidden>
            <Hidden smUp={true}>
                <Box sx={{ width: '100%', borderRadius: '15px', flexDirection: 'column', background: palette.background.paper, padding: '20px 18px', gap: '25px', mt: '30px' }}>
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                        <ResponsiveCard
                            title="Market Value"
                            value={cardValues?.result?.totalMarketValue < 0 ? `- ${formatCurrency({ value: Math.abs(cardValues?.result?.totalMarketValue ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: cardValues?.result?.totalMarketValue ?? 0, locale, currencyCode })}`}
                        />
                        <ResponsiveCard
                            title="Invested Value"
                            value={cardValues?.result?.totalInvestedValue < 0 ? `- ${formatCurrency({ value: Math.abs(cardValues?.result?.totalInvestedValue ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: cardValues?.result?.totalInvestedValue ?? 0, locale, currencyCode })}`}
                            align="right"
                        />
                    </Box>
                    <Box sx={{ width: '100%', display: 'flex', flexDirection: 'row', justifyContent: 'space-between', mt: '25px' }}>
                        <ResponsiveCard
                            title="Gain/Loss"
                            value={cardValues?.result?.gainLoss < 0 ? `- ${formatCurrency({ value: Math.abs(cardValues?.result?.gainLoss ?? 0), locale, currencyCode })}` : `${formatCurrency({ value: cardValues?.result?.gainLoss ?? 0, locale, currencyCode })}`}
                            color={Number(cardValues?.result?.gainLoss ?? 0) >= 0 ? palette.success[800] : palette.error[500]}
                        />
                        <ResponsiveCard
                            title="Absolute Return %"
                            value={!Number.isNaN(Number(cardValues?.result?.percentage)) ?
                                `${Math.round(cardValues?.result?.percentage * 100) / 100} %` : '0%'}
                            color={Number(cardValues?.result?.percentage ?? 0) >= 0 ? palette.success[800] : palette.error[500]}
                            align="right"
                        />
                    </Box>
                </Box>
            </Hidden>
            {/* <Box mt={'45px'} sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                gap: '10px',
                paddingBottom: '15px',
                borderBottom: `1px solid #CDCDCD`,
                overflowX: 'auto',
                width: '100%',
                '&::-webkit-scrollbar': {
                    display: 'none'
                }
            }}>
                {tabsList.map((item, index) => {
                    return (
                        <Button
                            variant={selectTab === item ? "contained" : "text"}
                            key={item}
                            onClick={() => handleChangeTab(item)}
                            sx={(theme: Theme) => ({
                                ...typography.body_500,
                                padding: '5px 15px',
                                background: selectTab === item ? theme.palette.primary[500] : 'transparent',
                                borderRadius: '7px',
                                outline: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                color: selectTab === item ? theme.palette.textDark.primary : theme.palette.text.secondary,
                                textWrap: 'nowrap'
                            })}
                        >
                            {item}
                        </Button>
                    )
                })}
            </Box> */}
            <TabContext value={selectTab}>
                <Tabs
                    value={selectTab}
                    onChange={handleChangeTab}
                    variant="scrollable"
                    scrollButtons="auto"
                    allowScrollButtonsMobile
                    TabIndicatorProps={{
                        sx: {
                            display: 'none',
                        }
                    }}
                    aria-label="scrollable auto tabs example"
                    sx={{
                        mt: '45px',
                        borderBottom: `1px solid #CDCDCD`,
                        paddingBottom: '15px',
                    }}
                >
                    {tabsList.map((item, index) => {
                        const tabId = `mf-tab-${item.replace(/\s+/g, '-').toLowerCase()}`
                        return (
                            <Tab
                                key={item}
                                id={tabId}
                                label={item}
                                value={item}
                                sx={(theme: Theme) => ({
                                    ...typography.body_500,
                                    lineHeight: '14px',
                                    textTransform: 'capitalize',
                                    color: theme.palette.customColors.infoCardTitle,
                                    '&.MuiTab-root ': {
                                        minHeight: '10px'
                                    },
                                    '&.Mui-selected': {
                                        color: theme.palette.textDark.primary,
                                        background: theme.palette.primary[500],
                                        borderRadius: '7px',
                                    },
                                })}
                                aria-controls="mf-tabpanel"
                            />
                        )
                    })}
                </Tabs>
            </TabContext>
            <Box
                id="mf-tabpanel"
                role="tabpanel"
                aria-labelledby={`mf-tab-${selectTab.replace(/\s+/g, '-').toLowerCase()}`}
                mt={'25px'}
            >
                {selectTab === "Overview" && (!isLoading && (zeroStateCheck || portfolioDoesntExist) ?
                    <Box
                        sx={(theme: Theme) => ({
                            backgroundColor: theme.palette.common.white,
                            borderRadius: "25px",
                            padding: "25px 30px",
                            height: "50vh"
                        })}
                    >
                        <ZeroStateInvestment isSuccess={portfolioDoesntExist} />
                    </Box> :
                    <OverView clientCode={navState?.clientCode} loading={isLoading} />
                )}
                {selectTab === "Holdings" && (!isLoading && (zeroStateCheck || portfolioDoesntExist) ?
                    <Box
                        sx={(theme: Theme) => ({
                            backgroundColor: theme.palette.common.white,
                            borderRadius: "25px",
                            padding: "25px 30px",
                            height: "50vh"
                        })}
                    >
                        <ZeroStateInvestment isSuccess={portfolioDoesntExist} />
                    </Box> :
                    <Holdings clientCode={navState?.clientCode} />
                )}
                {selectTab === 'Orders' && <Orders clientCode={navState?.clientCode} />}
                {selectTab === 'Recommendations' && <Recommendations clientCode={navState?.clientCode} />}
                {selectTab === 'Manage Mandates' && <Mandates clientCode={navState?.clientCode} />}
                {selectTab === "Systematic Plans" && <SystematicPlans clientCode={navState?.clientCode ?? ""} />}
            </Box>
        </Box >
    )
}

type CardProps = {
    Icons: (props: React.SVGProps<SVGSVGElement>) => JSX.Element
    title: string
    value: string
    color?: string
    percent?: boolean
    percentageValue?: number
    isLoading?: boolean
}

export const Card = (props: CardProps) => {
    const { Icons, title, value, color, percent, percentageValue, isLoading } = props
    const { palette, typography } = useTheme()
    return (
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: '10px', borderRadius: '15px', padding: '0px 18px', height: '102px', background: palette.common.white, boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.05)' }} >
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <Icons />
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '10px', width: '80%', }}>
                {isLoading ? <>
                    <Skeleton animation="wave" sx={{ width: '60%' }} />
                    <Skeleton animation="wave" sx={{ width: '100%' }} />
                </> : <>
                    <Typography variant="h2" color={palette.text.primary} sx={{ lineHeight: '14px', fontWeight: '500' }}>{title}</Typography>
                    {!percent ? <Typography variant="body_500" color={color ? color : palette.text.primary}>{value}</Typography> : <Typography variant="body_500" color={color ? color : palette.text.primary}>{`${value} (${percentageValue ? percentageValue.toFixed(2) : '0'}%)`}</Typography>}
                </>}
            </Box>
        </Box>
    )
}

export const ResponsiveCard = (props: { align?: 'left' | 'right', title: string, value: string, color?: string }) => {
    const { palette, typography } = useTheme()
    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: '5px' }}>
            <Typography variant="h2" align={props.align ? props.align : 'left'} color={palette.text.secondary} sx={{ lineHeight: '14px', fontWeight: '500' }}>{props.title}</Typography>
            <Typography variant="body_500" color={props.color || palette.text.primary}>{props.value}</Typography>
        </Box>
    )
}